import {
  getOmniMatches as getOmniMatchesGeneric,
  matchesOmniFilter as matchesOmniFilterGeneric,
  parseOmniQuery as parseOmniQueryGeneric,
  type OmniFieldDef,
  type OmniFields,
  type OmniMatch,
  type ParsedOmniQuery,
} from '@lib/utils/omniFilter';
import { extractYouTubeId, getYouTubeVideoId } from '@lib/utils/youtube';
import type { Media } from '@typings/Media';
import type { MediaTableRow } from '../components/LibraryContentMediaTable/cells/types';

/**
 * Media binding for the generic omnifilter engine (`@lib/utils/omniFilter`).
 *
 * A media row is shown if **any** space-separated token matches by:
 *   - substring of the title/filename (case-insensitive), OR
 *   - exact YouTube video ID (token is a raw 11-char ID or a YouTube URL), OR
 *   - exact media `_id`.
 *
 * This module re-exports the generic functions pre-bound to {@link MEDIA_OMNI_FIELDS}
 * so existing call sites keep their original shape (`parseOmniQuery(query)` etc.).
 */

export type { OmniMatch, ParsedOmniQuery } from '@lib/utils/omniFilter';

export type MediaOmniReason = 'name' | 'youtubeId' | 'mediaId';

export const MEDIA_OMNI_FIELDS: readonly OmniFieldDef<MediaOmniReason>[] = [
  { reason: 'name', label: 'name', mode: 'substring' },
  { reason: 'youtubeId', label: 'YouTube ID', mode: 'exact', transform: extractYouTubeId },
  { reason: 'mediaId', label: 'media ID', mode: 'exact' },
];

/**
 * Fields the media omnifilter matches against. Deliberately decoupled from the full
 * `Media` shape so in-flight upload rows (which only have a filename, no backend
 * `_id`/origin yet) can be matched too.
 */
export type OmniFilterFields = OmniFields<MediaOmniReason>;

export const parseOmniQuery = (
  query: string | null | undefined,
  matchAll = false,
): ParsedOmniQuery | null => parseOmniQueryGeneric(query, MEDIA_OMNI_FIELDS, matchAll);

export const matchesOmniFilter = (fields: OmniFilterFields, parsed: ParsedOmniQuery): boolean =>
  matchesOmniFilterGeneric(fields, parsed, MEDIA_OMNI_FIELDS);

export const getOmniMatches = (
  fields: OmniFilterFields,
  parsed: ParsedOmniQuery,
): OmniMatch<MediaOmniReason>[] => getOmniMatchesGeneric(fields, parsed, MEDIA_OMNI_FIELDS);

/** Extract the omnifilter match fields from a full Media / table-row object. */
export function getOmniFields(media: Media | MediaTableRow): OmniFilterFields {
  return {
    name: media.metadata?.name ?? media.metadata?.filename,
    youtubeId: getYouTubeVideoId(media as MediaTableRow),
    mediaId: media._id,
  };
}

/**
 * Folder binding: the same bar narrows the open folder's subfolders by name, so typing "2019"
 * leaves the "2019" folder beside the media it matched. A folder has no IDs a user would paste,
 * so name is its only field.
 */
const FOLDER_OMNI_FIELDS: readonly OmniFieldDef<'name'>[] = [
  { reason: 'name', label: 'name', mode: 'substring' },
];

export const folderMatchesOmniFilter = (
  name: string,
  query: string | null | undefined,
  matchAll = false,
): boolean => {
  const parsed = parseOmniQueryGeneric(query, FOLDER_OMNI_FIELDS, matchAll);
  return !parsed || matchesOmniFilterGeneric({ name }, parsed, FOLDER_OMNI_FIELDS);
};
